import React, { useState, useEffect } from 'react';
import { 
  Card, CardHeader, CardContent, Button, Typography, Alert, Box, 
  Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel,
  Divider, Chip
} from '@mui/material';
import { Email, Link as LinkIcon, LinkOff, AutoMode, Schedule } from '@mui/icons-material';
import { gmailApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import LoadingButton from './LoadingButton';

const GmailConnection = () => {
  const [status, setStatus] = useState({
    connected: false,
    gmailEmail: null,
    automationEnabled: false,
    autoSubmit: false,
    lastSyncAt: null,
    lastAutomationRun: null
  });
  const [automationSettings, setAutomationSettings] = useState({
    automationEnabled: false,
    autoSubmit: false,
    defaultAccountType: 'bank_account',
    timezone: 'UTC',
    automationHour: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await gmailApi.getStatus();
      setStatus(response.data);
      setAutomationSettings({
        automationEnabled: response.data.automationEnabled || false,
        autoSubmit: response.data.autoSubmit || false,
        defaultAccountType: 'bank_account',
        timezone: response.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        automationHour: response.data.automationHour !== undefined ? response.data.automationHour : new Date().getHours()
      });
    } catch (error) {
      console.error('Failed to load Gmail status:', error);
      showError('Failed to load Gmail connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await gmailApi.getAuthUrl();
      window.location.href = response.data.authUrl;
    } catch (error) {
      showError('Failed to start Gmail connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? This will disable automation.')) return;
    
    try {
      await gmailApi.disconnect();
      setStatus({ ...status, connected: false, automationEnabled: false, autoSubmit: false });
      setAutomationSettings({ ...automationSettings, automationEnabled: false, autoSubmit: false });
      showSuccess('Gmail disconnected successfully');
    } catch (error) {
      showError('Failed to disconnect Gmail');
    }
  };

  const handleSaveAutomation = async () => {
    setSaving(true);
    try {
      await gmailApi.updateAutomation(automationSettings);
      setStatus({ ...status, ...automationSettings });
      showSuccess('Automation settings saved successfully');
    } catch (error) {
      showError('Failed to save automation settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          avatar={<Email color={status.connected ? 'success' : 'disabled'} />}
          title="Gmail connection (optional)"
          subheader="Gives PinePocket read access to your whole mailbox. Forwarding is the recommended way; use this only to import older emails or if you prefer it."
          action={
            status.connected ? (
              <Chip label="Connected" color="success" size="small" />
            ) : (
              <Chip label="Not Connected" color="default" size="small" />
            )
          }
        />
        <CardContent>
          {status.connected ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Gmail is connected. You can now fetch transaction emails manually or enable automation.
              </Alert>
              
              {status.gmailEmail && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Connected account:</strong> {status.gmailEmail}
                </Typography>
              )}
              
              {status.lastSyncAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Last sync:</strong> {formatDate(status.lastSyncAt)}
                </Typography>
              )}
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOff />}
                onClick={handleDisconnect}
              >
                Disconnect Gmail
              </Button>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Connect your Gmail account to automatically fetch and process transaction emails.
                You'll be redirected to Google to grant read-only access to your emails.
              </Alert>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleConnect}
                disabled={loading}
              >
                Connect Gmail
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {status.connected && (
        <Card>
          <CardHeader 
            avatar={<AutoMode color={automationSettings.automationEnabled ? 'primary' : 'disabled'} />}
            title="Gmail import settings"
            subheader="Account type and optional daily import"
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Processing Defaults
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Default Account Type</InputLabel>
                <Select
                  value={automationSettings.defaultAccountType}
                  onChange={(e) => setAutomationSettings({ 
                    ...automationSettings, 
                    defaultAccountType: e.target.value 
                  })}
                  label="Default Account Type"
                >
                  <MenuItem value="bank_account">Bank Account</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                </Select>
              </FormControl>

              <Divider />

              <Typography variant="subtitle2" color="text.secondary">
                Daily Automation
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={automationSettings.automationEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      // Auto-detect timezone and hour when first enabling
                      if (enabled && !status.automationEnabled) {
                        setAutomationSettings({ 
                          ...automationSettings, 
                          automationEnabled: true,
                          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                          automationHour: new Date().getHours()
                        });
                      } else {
                        setAutomationSettings({ 
                          ...automationSettings, 
                          automationEnabled: enabled 
                        });
                      }
                    }}
                    color="primary"
                  />
                }
                label="Enable Daily Automation"
              />

              {automationSettings.automationEnabled && (
                <>
                  <Alert severity="info" icon={<Schedule />}>
                    Automation runs daily at your selected time and fetches transactions 
                    from the previous calendar day in your timezone.
                  </Alert>

                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={automationSettings.timezone}
                      onChange={(e) => setAutomationSettings({ 
                        ...automationSettings, 
                        timezone: e.target.value 
                      })}
                      label="Timezone"
                    >
                      <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                      <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                      <MenuItem value="Europe/London">London (GMT)</MenuItem>
                      <MenuItem value="Europe/Paris">Central European Time (CET)</MenuItem>
                      <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
                      <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
                      <MenuItem value="Asia/Singapore">Singapore (SGT)</MenuItem>
                      <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                      <MenuItem value="Australia/Sydney">Sydney (AEST)</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Automation Time</InputLabel>
                    <Select
                      value={automationSettings.automationHour}
                      onChange={(e) => setAutomationSettings({ 
                        ...automationSettings, 
                        automationHour: e.target.value 
                      })}
                      label="Automation Time"
                    >
                      {Array.from({length: 24}, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00 ({i < 12 ? (i === 0 ? '12 AM' : i + ' AM') : (i === 12 ? '12 PM' : (i - 12) + ' PM')})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Divider />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={automationSettings.autoSubmit}
                        onChange={(e) => setAutomationSettings({ 
                          ...automationSettings, 
                          autoSubmit: e.target.checked 
                        })}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">Auto-Submit Transactions</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {automationSettings.autoSubmit 
                            ? 'Transactions will be directly added to your database without review'
                            : 'Transactions will be saved for review in Processing Jobs'
                          }
                        </Typography>
                      </Box>
                    }
                  />

                  {automationSettings.autoSubmit && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Auto-submitted transactions will be marked with a flag so you can filter them later.
                    </Alert>
                  )}

                  {status.lastAutomationRun && (
                    <Typography variant="body2" color="text.secondary">
                      Last automation run: {formatDate(status.lastAutomationRun)}
                    </Typography>
                  )}
                </>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={loadStatus}
                >
                  Reset
                </Button>
                <LoadingButton
                  variant="contained"
                  onClick={handleSaveAutomation}
                  loading={saving}
                >
                  Save Settings
                </LoadingButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GmailConnection;

